import { useEffect, useState } from "react";
import {
  Inbox,
  Mail,
  Trash2,
  CheckCircle2,
  Circle,
  Building2,
  RefreshCw,
  Send,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { api, formatApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

export default function ContactMessages() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread | read
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/contact", { params: { limit: 500 } });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const unreadCount = items.filter((m) => !m.read).length;
  const visible = items.filter((m) => {
    if (filter === "unread") return !m.read;
    if (filter === "read") return m.read;
    return true;
  });

  async function setRead(id, read) {
    try {
      const { data } = await api.patch(`/contact/${id}`, { read });
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, ...data } : m)));
      if (selected?.id === id) setSelected({ ...selected, ...data });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function remove(id) {
    if (!window.confirm("¿Eliminar este mensaje? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/contact/${id}`);
      setItems((prev) => prev.filter((m) => m.id !== id));
      if (selected?.id === id) setOpen(false);
      toast.success("Mensaje eliminado");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  function openMessage(msg) {
    setSelected(msg);
    setOpen(true);
    if (!msg.read) setRead(msg.id, true);
  }

  return (
    <div className="space-y-6" data-testid="contact-messages-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="kpi-label">{t("nav.contact_messages")}</p>
          <h1 className="text-2xl font-bold tracking-tight">Bandeja de contactos</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Mensajes recibidos desde el formulario público de la landing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            data-testid="contact-refresh"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Actualizar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard label="Total" value={items.length} icon={Inbox} />
        <KpiCard
          label="Sin leer"
          value={unreadCount}
          icon={Circle}
          highlight={unreadCount > 0}
        />
        <KpiCard label="Notificados por email" value={items.filter((m) => m.notified).length} icon={Send} />
      </div>

      <div className="flex items-center gap-2">
        <FilterBtn label="Todos" active={filter === "all"} onClick={() => setFilter("all")} count={items.length} />
        <FilterBtn label="Sin leer" active={filter === "unread"} onClick={() => setFilter("unread")} count={unreadCount} />
        <FilterBtn label="Leídos" active={filter === "read"} onClick={() => setFilter("read")} count={items.length - unreadCount} />
      </div>

      <Card className="card-soft">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">Estado</TableHead>
                <TableHead>Remitente</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Necesidad</TableHead>
                <TableHead>Recibido</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                    Cargando…
                  </TableCell>
                </TableRow>
              ) : visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
                    <Inbox className="h-5 w-5 mx-auto mb-2" />
                    {filter === "unread"
                      ? "No tienes mensajes sin leer."
                      : filter === "read"
                      ? "Aún no has leído ningún mensaje."
                      : "Aún no hay mensajes recibidos."}
                  </TableCell>
                </TableRow>
              ) : (
                visible.map((m) => (
                  <TableRow
                    key={m.id}
                    className={`cursor-pointer hover:bg-muted/40 ${!m.read ? "font-medium" : ""}`}
                    onClick={() => openMessage(m)}
                    data-testid={`contact-row-${m.id}`}
                  >
                    <TableCell>
                      {m.read ? (
                        <CheckCircle2
                          className="h-4 w-4 text-muted-foreground"
                          aria-label="Leído"
                        />
                      ) : (
                        <span
                          className="inline-block h-2 w-2 rounded-full bg-primary"
                          aria-label="Sin leer"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">{m.name}</span>
                        <span className="text-xs text-muted-foreground font-mono">{m.email}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {m.company ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Building2 className="h-3 w-3 text-muted-foreground" />
                          {m.company}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs max-w-[420px] truncate">
                      {m.measurement}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      {new Date(m.created_at).toLocaleString()}
                      {!m.notified && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"
                          title={m.notify_status || "No se reenvió por email"}
                        >
                          <AlertCircle className="h-3 w-3" />
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div
                        className="inline-flex gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setRead(m.id, !m.read)}
                          title={m.read ? "Marcar como no leído" : "Marcar como leído"}
                          data-testid={`toggle-read-${m.id}`}
                        >
                          {m.read ? (
                            <Circle className="h-4 w-4" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4" />
                          )}
                        </Button>
                        <a
                          href={`mailto:${m.email}?subject=${encodeURIComponent(
                            "Re: Tu consulta a Olimpia Sensor"
                          )}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            title="Responder por email"
                            data-testid={`reply-${m.id}`}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        </a>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => remove(m.id)}
                          title="Eliminar"
                          data-testid={`delete-${m.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto"
          data-testid="contact-detail-sheet"
        >
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle>{selected.name}</SheetTitle>
                <SheetDescription className="flex flex-wrap gap-2 items-center">
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-primary hover:underline font-mono text-xs"
                  >
                    {selected.email}
                  </a>
                  {selected.company && (
                    <Badge variant="outline" className="text-[10px]">
                      <Building2 className="h-3 w-3 mr-1" />
                      {selected.company}
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {new Date(selected.created_at).toLocaleString()}
                  </span>
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-5">
                <Field label="¿Qué necesita medir?" value={selected.measurement} />
                {selected.conditions && (
                  <Field label="Condiciones del proyecto" value={selected.conditions} />
                )}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-md border p-3">
                    <div className="kpi-label">Notificación email</div>
                    <div className="mt-1 flex items-center gap-1.5">
                      {selected.notified ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                          <span>Enviado</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                          <span className="text-muted-foreground truncate" title={selected.notify_status}>
                            No enviado
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="rounded-md border p-3">
                    <div className="kpi-label">Estado</div>
                    <div className="mt-1">
                      {selected.read ? (
                        <Badge variant="outline">Leído</Badge>
                      ) : (
                        <Badge className="bg-primary/10 text-primary border-primary/30" variant="outline">
                          Sin leer
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <a
                    href={`mailto:${selected.email}?subject=${encodeURIComponent(
                      "Re: Tu consulta a Olimpia Sensor"
                    )}`}
                    className="flex-1"
                  >
                    <Button className="w-full" data-testid="sheet-reply">
                      <Mail className="h-4 w-4 mr-2" /> Responder por email
                    </Button>
                  </a>
                  <Button
                    variant="outline"
                    onClick={() => setRead(selected.id, !selected.read)}
                    data-testid="sheet-toggle-read"
                  >
                    {selected.read ? "Marcar sin leer" : "Marcar leído"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, highlight }) {
  return (
    <Card className={`card-soft ${highlight ? "border-primary/40" : ""}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div
          className={`h-10 w-10 rounded-md grid place-items-center ${
            highlight ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
          }`}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="kpi-label">{label}</p>
          <p className="text-xl font-bold tabular-nums">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function FilterBtn({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`filter-${label.toLowerCase()}`}
      className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-2 ${
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-background text-muted-foreground hover:text-foreground border-border"
      }`}
    >
      {label}
      <span
        className={`text-[10px] tabular-nums px-1.5 rounded-full ${
          active ? "bg-primary-foreground/20" : "bg-muted"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="kpi-label">{label}</div>
      <div className="mt-1.5 text-sm whitespace-pre-wrap rounded-md border bg-muted/30 px-3 py-2.5 leading-relaxed">
        {value}
      </div>
    </div>
  );
}
